import React, { useEffect, useState } from 'react';
import { Shield, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useArenaCohosts } from '@/hooks/useArenaCohosts';
import { cn } from '@/lib/utils';

interface CohostInviteNotificationProps {
  arenaId: string;
  userId: string;
}

export const CohostInviteNotification: React.FC<CohostInviteNotificationProps> = ({
  arenaId,
  userId,
}) => {
  const { pendingInvites, acceptInvite, declineInvite } = useArenaCohosts(arenaId);
  const [myInvite, setMyInvite] = useState<typeof pendingInvites[0] | null>(null);
  const [isResponding, setIsResponding] = useState(false);

  useEffect(() => {
    const invite = pendingInvites.find((i) => i.user_id === userId);
    setMyInvite(invite || null);
  }, [pendingInvites, userId]);

  const handleAccept = async () => {
    if (!myInvite) return;
    setIsResponding(true);
    await acceptInvite(myInvite.id);
    setIsResponding(false);
  };

  const handleDecline = async () => {
    if (!myInvite) return;
    setIsResponding(true);
    await declineInvite(myInvite.id);
    setIsResponding(false);
  };

  if (!myInvite) return null;

  return (
    <div
      className={cn(
        "fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[90%] max-w-sm",
        "animate-in slide-in-from-top-4 duration-300"
      )}
    >
      <div className="bg-gradient-to-r from-primary/10 to-secondary/10 backdrop-blur-xl border border-primary/20 rounded-2xl p-4 shadow-xl">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
            <Shield className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold">Co-host Invitation</h3>
            <p className="text-xs text-muted-foreground">
              You've been invited to co-host this arena
            </p>
          </div>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          As a co-host, you'll be able to moderate participants, spotlight speakers, and manage the chat.
        </p>

        <div className="flex gap-2">
          <Button
            onClick={handleAccept}
            disabled={isResponding}
            className="flex-1 gap-2 bg-primary hover:bg-primary/90"
          >
            <Check className="w-4 h-4" />
            Accept
          </Button>
          <Button
            onClick={handleDecline}
            disabled={isResponding}
            variant="outline"
            className="flex-1 gap-2"
          >
            <X className="w-4 h-4" />
            Decline
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CohostInviteNotification;
